import React, { useEffect, useState } from 'react';
import { usePurchaseOrdersStore } from '../../../../store/purchaseOrdersStore';
import { useSkusStore } from '../../../../store/skusStore';
import { Supplier, listSuppliers, createSupplier } from '../../../../services/purchaseOrdersApi';
import styles from './CreatePurchaseOrderModal.module.css';

interface CreatePurchaseOrderModalProps {
  open: boolean;
  onClose: () => void;
}

interface LineItem {
  sku_id: string;
  quantity_ordered: string;
  unit_cost: string;
}

const emptyLine = (): LineItem => ({ sku_id: '', quantity_ordered: '1', unit_cost: '' });

export const CreatePurchaseOrderModal: React.FC<CreatePurchaseOrderModalProps> = ({ open, onClose }) => {
  const [supplierId, setSupplierId] = useState<string>('');
  const [expectedDate, setExpectedDate] = useState('');
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<LineItem[]>([emptyLine()]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Inline "add supplier" CTA state.
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [newSupplierEmail, setNewSupplierEmail] = useState('');
  const [newSupplierPhone, setNewSupplierPhone] = useState('');
  const [savingSupplier, setSavingSupplier] = useState(false);
  const [supplierError, setSupplierError] = useState<string | null>(null);

  const createPO = usePurchaseOrdersStore((s) => s.create);
  const skus = useSkusStore((s) => s.items);
  const fetchSkus = useSkusStore((s) => s.fetchList);

  useEffect(() => {
    if (!open) {
      setSupplierId('');
      setExpectedDate('');
      setNotes('');
      setLines([emptyLine()]);
      setError(null);
      setSubmitting(false);
      setAddingSupplier(false);
      setNewSupplierName('');
      setNewSupplierEmail('');
      setNewSupplierPhone('');
      setSavingSupplier(false);
      setSupplierError(null);
      return;
    }
    if (skus.length === 0) fetchSkus().catch(() => undefined);
    setLoadingSuppliers(true);
    listSuppliers()
      .then((list) => setSuppliers(list))
      .catch(() => setSuppliers([]))
      .finally(() => setLoadingSuppliers(false));
  }, [open, skus.length, fetchSkus]);

  if (!open) return null;

  const updateLine = (idx: number, patch: Partial<LineItem>) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  };

  const handleSkuChange = (idx: number, skuId: string) => {
    const sku = skus.find((s) => String(s.id) === skuId);
    updateLine(idx, {
      sku_id: skuId,
      unit_cost: sku ? String(sku.unit_cost) : '',
    });
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (idx: number) => setLines((prev) => prev.filter((_, i) => i !== idx));

  const handleSaveSupplier = async () => {
    const name = newSupplierName.trim();
    if (!name) { setSupplierError('Supplier name is required'); return; }
    setSupplierError(null);
    setSavingSupplier(true);
    try {
      const created = await createSupplier(name, {
        email: newSupplierEmail.trim() || undefined,
        phone: newSupplierPhone.trim() || undefined,
      });
      const supplier: Supplier = {
        id: created.id,
        name: created.name,
        email: newSupplierEmail.trim() || null,
        phone: newSupplierPhone.trim() || null,
        is_active: true,
      };
      // Add (or replace, since createSupplier upserts on name) and select it.
      setSuppliers((prev) => [supplier, ...prev.filter((s) => s.id !== supplier.id)]);
      setSupplierId(String(supplier.id));
      setAddingSupplier(false);
      setNewSupplierName('');
      setNewSupplierEmail('');
      setNewSupplierPhone('');
    } catch (err) {
      setSupplierError(err instanceof Error ? err.message : 'Failed to add supplier');
    } finally {
      setSavingSupplier(false);
    }
  };

  const total = lines.reduce((sum, l) => {
    const q = parseFloat(l.quantity_ordered) || 0;
    const c = parseFloat(l.unit_cost) || 0;
    return sum + q * c;
  }, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const valid = lines.filter((l) => l.sku_id && parseFloat(l.quantity_ordered) > 0);
    if (valid.length === 0) {
      setError('Add at least one line item with quantity > 0');
      return;
    }
    for (const l of valid) {
      if (!parseFloat(l.unit_cost) || parseFloat(l.unit_cost) < 0) {
        setError('Each line item needs a positive unit cost');
        return;
      }
    }
    setError(null);
    setSubmitting(true);
    try {
      await createPO({
        supplier_id: supplierId ? Number(supplierId) : undefined,
        expected_date: expectedDate || undefined,
        notes: notes.trim() || undefined,
        items: valid.map((l) => ({
          sku_id: Number(l.sku_id),
          quantity_ordered: parseFloat(l.quantity_ordered),
          unit_cost: parseFloat(l.unit_cost),
        })),
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create purchase order');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="dialog" aria-modal="true">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>New Purchase Order</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        <form onSubmit={handleSubmit} className={styles.form}>
          {error && <p className={styles.error}>{error}</p>}

          <div className={styles.row}>
            <label className={styles.field}>
              <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Supplier <span className={styles.muted}>(optional)</span>
                <button
                  type="button"
                  onClick={() => { setAddingSupplier((v) => !v); setSupplierError(null); }}
                  style={{ border: 'none', background: 'transparent', color: '#2B7FFF', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0 }}
                >
                  {addingSupplier ? 'Cancel' : '+ Add Supplier'}
                </button>
              </span>
              <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} disabled={loadingSuppliers || addingSupplier}>
                <option value="">{loadingSuppliers ? 'Loading…' : '— None —'}</option>
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span>Expected Delivery Date</span>
              <input type="date" value={expectedDate} onChange={(e) => setExpectedDate(e.target.value)} />
            </label>
          </div>

          {addingSupplier && (
            <div style={{ border: '1px solid #E5E7EB', borderRadius: 8, padding: 12, marginBottom: 12, background: '#F8FAFC' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1.5fr auto', gap: 8, alignItems: 'end' }}>
                <label className={styles.field}>
                  <span>Name <span className={styles.required}>*</span></span>
                  <input value={newSupplierName} onChange={(e) => setNewSupplierName(e.target.value)} placeholder="Supplier name" />
                </label>
                <label className={styles.field}>
                  <span>Email</span>
                  <input type="email" value={newSupplierEmail} onChange={(e) => setNewSupplierEmail(e.target.value)} placeholder="optional" />
                </label>
                <label className={styles.field}>
                  <span>Phone</span>
                  <input value={newSupplierPhone} onChange={(e) => setNewSupplierPhone(e.target.value)} placeholder="optional" />
                </label>
                <button
                  type="button"
                  className={styles.addLineBtn}
                  onClick={handleSaveSupplier}
                  disabled={savingSupplier || !newSupplierName.trim()}
                >
                  {savingSupplier ? 'Saving…' : 'Save'}
                </button>
              </div>
              {supplierError && <p className={styles.error} style={{ marginBottom: 0 }}>{supplierError}</p>}
            </div>
          )}

          <label className={styles.field}>
            <span>Notes</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} maxLength={500} />
          </label>

          <div className={styles.linesHeader}>
            <span className={styles.linesTitle}>Line Items <span className={styles.required}>*</span></span>
            <button type="button" className={styles.addLineBtn} onClick={addLine}>+ Add Line</button>
          </div>

          <div className={styles.linesTable}>
            <div className={styles.lineHead}>
              <span>Product</span>
              <span>Qty</span>
              <span>Unit Cost</span>
              <span>Line Total</span>
              <span></span>
            </div>
            {lines.map((line, idx) => {
              const qty = parseFloat(line.quantity_ordered) || 0;
              const cost = parseFloat(line.unit_cost) || 0;
              return (
                <div key={idx} className={styles.lineRow}>
                  <select value={line.sku_id} onChange={(e) => handleSkuChange(idx, e.target.value)} required>
                    <option value="">— Select product —</option>
                    {skus.map((s) => (
                      <option key={s.id} value={s.id}>{s.sku_code} — {s.product_name}</option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={line.quantity_ordered}
                    onChange={(e) => updateLine(idx, { quantity_ordered: e.target.value })}
                    required
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={line.unit_cost}
                    onChange={(e) => updateLine(idx, { unit_cost: e.target.value })}
                    required
                  />
                  <span className={styles.lineTotal}>${(qty * cost).toFixed(2)}</span>
                  <button
                    type="button"
                    className={styles.removeLineBtn}
                    onClick={() => removeLine(idx)}
                    disabled={lines.length === 1}
                    aria-label="Remove line"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>

          <div className={styles.totalRow}>
            <strong>Order Total: ${total.toFixed(2)}</strong>
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancel} onClick={onClose} disabled={submitting}>Cancel</button>
            <button type="submit" className={styles.submit} disabled={submitting}>
              {submitting ? 'Creating…' : 'Create Purchase Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePurchaseOrderModal;
